import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { newsItems } from "@/mocks/newsData";
import { Link } from "react-router-dom";

const categoryColors: Record<string, string> = {
  publication: "bg-tie-teal/10 text-tie-teal",
  conference: "bg-tie-navy/10 text-tie-navy",
  announcement: "bg-amber-50 text-amber-700",
  milestone: "bg-emerald-50 text-emerald-700",
};

function getYouTubeThumbnail(url: string) {
  let id = "";
  const match = url.match(/[?&]v=([^&]+)/);
  if (match) {
    id = match[1];
  } else {
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) id = shortMatch[1];
  }
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function isVideoUrl(url: string) {
  return url.includes("youtube.com") || url.includes("youtu.be");
}

export default function PressFeatured() {
  const pressItems = newsItems.filter((n) => n.source === "Textile Today");
  const featured = pressItems.filter((n) => n.featured);
  // Prioritise the grand launch article first
  const grandLaunch = pressItems.find((n) => n.id === "tie-officially-launched");
  const otherFeatured = featured.filter((n) => n.id !== "tie-officially-launched");
  const orderedFeatured = grandLaunch ? [grandLaunch, ...otherFeatured] : featured;
  const [primary, secondary] = orderedFeatured;

  return (
    <section className="bg-tie-grey-light py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <ScrollReveal>
            <div className="mb-8 md:mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-5">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Featured Releases
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-tie-text-primary">
                Headlines
              </h2>
            </div>
          </ScrollReveal>

          {/* Featured Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 md:gap-6">
            {/* Primary Featured */}
            {primary && (
              <ScrollReveal className="lg:col-span-3">
                <article className="group h-full bg-white rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300">
                  <Link
                    to={`/press-release/${primary.id}`}
                    className="block relative h-56 md:h-72 overflow-hidden"
                  >
                    {primary.image ? (
                      <img
                        src={primary.image}
                        alt={primary.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : isVideoUrl(primary.externalUrl) ? (
                      <>
                        <img
                          src={getYouTubeThumbnail(primary.externalUrl)}
                          alt={primary.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                            <i className="ri-play-fill text-3xl text-white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-tie-navy/80 to-tie-navy flex items-center justify-center">
                        <div className="text-center px-4">
                          <i className="ri-newspaper-line text-4xl text-white/40 mb-2" />
                          <p className="text-sm text-white/60">{primary.source}</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/60 via-tie-navy/20 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${categoryColors[primary.category]}`}
                      >
                        {primary.tag}
                      </span>
                      {primary.id === "tie-officially-launched" && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-500 text-white">
                          <i className="ri-star-fill text-[9px]" />
                          Grand Launch
                        </span>
                      )}
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm">
                        <i className="ri-arrow-right-line text-xs text-white" />
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3 text-xs text-white/80 mb-2">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {primary.date}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line" />
                          {primary.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="p-5 md:p-6">
                    <Link
                      to={`/press-release/${primary.id}`}
                      className="block"
                    >
                      <h3 className="text-xl md:text-2xl font-semibold text-tie-text-primary mb-3 leading-snug group-hover:text-tie-teal transition-colors">
                        {primary.title}
                      </h3>
                    </Link>
                    <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">
                      {primary.excerpt}
                    </p>
                    <div className="mt-5 pt-4 border-t border-tie-grey-dark">
                      <Link
                        to={`/press-release/${primary.id}`}
                        className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
                      >
                        Read full story
                        <i className="ri-arrow-right-line ml-1.5 text-xs opacity-60" />
                      </Link>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            )}

            {/* Secondary Featured + Related Links */}
            <div className="lg:col-span-2 flex flex-col gap-5 md:gap-6">
              {secondary && (
                <ScrollReveal delay={150}>
                  <article className="group h-full bg-white rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300">
                    <Link
                      to={`/press-release/${secondary.id}`}
                      className="block relative h-44 md:h-52 overflow-hidden"
                    >
                      {secondary.image ? (
                        <img
                          src={secondary.image}
                          alt={secondary.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : isVideoUrl(secondary.externalUrl) ? (
                        <>
                          <img
                            src={getYouTubeThumbnail(secondary.externalUrl)}
                            alt={secondary.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                              <i className="ri-play-fill text-2xl text-white" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-tie-navy/80 to-tie-navy flex items-center justify-center">
                          <div className="text-center px-4">
                            <i className="ri-newspaper-line text-3xl text-white/40 mb-2" />
                            <p className="text-xs text-white/60">{secondary.source}</p>
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/50 to-transparent" />
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${categoryColors[secondary.category]}`}
                        >
                          {secondary.tag}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm">
                          <i className="ri-arrow-right-line text-[10px] text-white" />
                        </span>
                      </div>
                    </Link>
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 text-xs text-tie-text-muted mb-2">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {secondary.date}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line" />
                          {secondary.readTime}
                        </span>
                      </div>
                      <Link
                        to={`/press-release/${secondary.id}`}
                        className="block"
                      >
                        <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2 leading-snug group-hover:text-tie-teal transition-colors">
                          {secondary.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-tie-text-secondary leading-relaxed line-clamp-2">
                        {secondary.excerpt}
                      </p>
                      <div className="mt-4 pt-3 border-t border-tie-grey-dark">
                        <Link
                          to={`/press-release/${secondary.id}`}
                          className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
                        >
                          Read more
                          <i className="ri-arrow-right-line ml-1.5 text-xs opacity-60" />
                        </Link>
                      </div>
                    </div>
                  </article>
                </ScrollReveal>
              )}

              {/* Explore Related */}
              <ScrollReveal delay={250}>
                <div className="p-5 md:p-6 rounded-2xl bg-tie-navy text-white">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-4">
                    Explore Related
                  </h4>
                  <div className="space-y-3">
                    <Link
                      to="/piarc"
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <span className="text-sm font-medium">PIARC Research</span>
                      <i className="ri-arrow-right-line text-tie-teal-light" />
                    </Link>
                    <Link
                      to="/ijtai"
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <span className="text-sm font-medium">IJTAI Journal</span>
                      <i className="ri-arrow-right-line text-tie-teal-light" />
                    </Link>
                    <Link
                      to="/aircon"
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <span className="text-sm font-medium">AIRCon Conference</span>
                      <i className="ri-arrow-right-line text-tie-teal-light" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}